import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bus, Plus, LogOut, Building2, X, Users, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function AppShell({ children, operadores, reloadOperadores }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const [showNew, setShowNew] = useState(false);
  const [nombre, setNombre] = useState("");
  const [nombreCorto, setNombreCorto] = useState("");
  const [codigo, setCodigo] = useState("");

  const create = async () => {
    if (!nombre.trim()) return;
    try {
      const res = await api.post("/operadores", {
        nombre: nombre.trim(),
        nombre_corto: nombreCorto.trim() || nombre.trim(),
        codigo_operador: codigo ? Number(codigo) : null,
      });
      toast.success("Operador creado");
      setShowNew(false);
      setNombre(""); setNombreCorto(""); setCodigo("");
      await reloadOperadores();
      navigate(`/operador/${res.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 bg-secondary text-white h-screen sticky top-0 flex flex-col z-20" data-testid="sidebar">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/10">
          <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center"><Bus size={20} /></div>
          <div className="leading-tight">
            <div className="font-bold" style={{ fontFamily: "Work Sans" }}>ATTG Data Master</div>
            <div className="text-[10px] text-white/50">Servicios Urbanos</div>
          </div>
        </div>

        <div className="px-4 pt-5 pb-2 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-white/40">Operadores</span>
          {isAdmin && (
            <button onClick={() => setShowNew(true)} data-testid="new-operador-btn" className="h-6 w-6 rounded flex items-center justify-center hover:bg-white/10 text-white/70">
              <Plus size={16} />
            </button>
          )}
        </div>

        <nav className="flex-1 overflow-auto attg-scroll px-3 space-y-1">
          {operadores.map((op) => (
            <button
              key={op.id}
              onClick={() => navigate(`/operador/${op.id}`)}
              data-testid={`operador-nav-${op.id}`}
              className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
                id === op.id ? "bg-primary text-white" : "text-white/70 hover:bg-white/10"
              }`}
            >
              <Building2 size={15} className="flex-shrink-0" />
              <span className="truncate">{op.nombre_corto || op.nombre}</span>
            </button>
          ))}
          {operadores.length === 0 && (
            <p className="px-3 py-2 text-xs text-white/40">
              {isAdmin ? "Sin operadores. Crea el primero." : "Sin operador asignado."}
            </p>
          )}
        </nav>

        {isAdmin && (
          <div className="px-3 pb-1">
            <button
              onClick={() => navigate("/usuarios")}
              data-testid="users-nav-btn"
              className="w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 text-white/70 hover:bg-white/10 transition-colors"
            >
              <Users size={15} /> Accesos de usuario
            </button>
          </div>
        )}

        <div className="border-t border-white/10 p-3">
          <div className="px-2 mb-2">
            <div className="flex items-center gap-1.5">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wide bg-primary/30 text-white rounded px-1.5 py-0.5">
                <ShieldCheck size={10} /> {user?.role === "admin" ? "Admin" : user?.role === "consulta" ? "Consulta" : "Operador"}
              </span>
            </div>
            <div className="text-[11px] text-white/40 truncate">{user?.email}</div>
          </div>
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn" className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm text-white/70 hover:bg-white/10">
            <LogOut size={15} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col h-screen overflow-hidden">{children}</main>

      {/* New operador modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="new-operador-dialog">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowNew(false)} />
          <div className="relative z-10 w-full max-w-md rounded-lg bg-background shadow-xl border">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>Nuevo Operador</h3>
              <button onClick={() => setShowNew(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nombre *</label>
                <input value={nombre} onChange={(e) => setNombre(e.target.value)} data-testid="new-op-nombre" className="mt-1 h-9 w-full rounded-md border border-input px-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nombre corto</label>
                <input value={nombreCorto} onChange={(e) => setNombreCorto(e.target.value)} data-testid="new-op-corto" className="mt-1 h-9 w-full rounded-md border border-input px-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Código de operador</label>
                <input type="number" value={codigo} onChange={(e) => setCodigo(e.target.value)} data-testid="new-op-codigo" className="mt-1 h-9 w-full rounded-md border border-input px-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t">
              <button onClick={() => setShowNew(false)} className="inline-flex items-center rounded-md border border-border bg-card h-9 px-4 text-sm font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={create} data-testid="new-op-save" className="inline-flex items-center rounded-md bg-primary text-primary-foreground h-9 px-4 text-sm font-medium shadow-sm hover:bg-primary/90">Crear</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
